import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';

const DAY_LABELS_ID = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const DAY_LABELS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const MONTH_LABELS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Palette per event_type — keeps calendar dots consistent with card badges.
const TYPE_COLOR = {
  KEBAKTIAN:   '#D98A94',
  PUJA:        '#D98A94',
  DHARMA:      '#8B5CF6',
  MEDITASI:    '#3B82F6',
  RETRET:      '#10B981',
  PERAYAAN:    '#F59E0B',
  SOSIAL:      '#EC4899',
  KURSUS:      '#06B6D4',
};

function keyOf(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function parseISO(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(v => parseInt(v, 10));
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
}

export default function MediaCalendarWidget({ events = [], selectedDate, onSelectDate, lang = 'id' }) {
  const isID = lang !== 'en';
  const DAY_LABELS = isID ? DAY_LABELS_ID : DAY_LABELS_EN;
  const MONTH_LABELS = isID ? MONTH_LABELS_ID : MONTH_LABELS_EN;

  const today = useMemo(() => {
    const t = new Date();
    return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() };
  }, []);

  // Default cursor to the month of the nearest upcoming event; fall back to the
  // month with the most events, else today. Prevents "empty month" first impression
  // when all real events are in a future month (e.g., today = 28 Juli, event = 2 Agustus).
  const initialCursor = useMemo(() => {
    const parsed = events.map(e => parseISO(e.date_iso)).filter(Boolean);
    if (!parsed.length) return { y: today.y, m: today.m };
    const todayNum = today.y * 12 + today.m;
    const upcoming = parsed
      .map(p => ({ p, mNum: p.y * 12 + p.m }))
      .filter(x => x.mNum >= todayNum)
      .sort((a, b) => a.mNum - b.mNum);
    if (upcoming.length) return { y: upcoming[0].p.y, m: upcoming[0].p.m };
    const sorted = parsed.map(p => ({ p, mNum: p.y * 12 + p.m })).sort((a, b) => b.mNum - a.mNum);
    return { y: sorted[0].p.y, m: sorted[0].p.m };
  }, [events, today]);
  const [cursor, setCursor] = useState(initialCursor);
  const userNavigatedRef = useRef(false);

  // When events load asynchronously, jump the cursor to the initial (nearest-event)
  // month — but only if the user has not manually navigated yet.
  useEffect(() => {
    if (userNavigatedRef.current) return;
    setCursor(c => (c.y === initialCursor.y && c.m === initialCursor.m ? c : initialCursor));
  }, [initialCursor]);

  const goPrev = () => { userNavigatedRef.current = true; setCursor(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }); };
  const goNext = () => { userNavigatedRef.current = true; setCursor(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }); };
  const goToday = () => { userNavigatedRef.current = true; setCursor({ y: today.y, m: today.m }); };

  // Map: yyyy-mm-dd → [event, event, ...]
  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const p = parseISO(ev.date_iso);
      if (!p) continue;
      const arr = map.get(p.key) || [];
      arr.push(ev);
      map.set(p.key, arr);
    }
    return map;
  }, [events]);

  const eventCountThisMonth = useMemo(() => {
    let n = 0;
    for (const [k, arr] of eventsByDate) {
      const [y, m] = k.split('-').map(v => parseInt(v, 10));
      if (y === cursor.y && (m - 1) === cursor.m) n += arr.length;
    }
    return n;
  }, [eventsByDate, cursor]);

  // Build the day grid (6 rows × 7 cols, 42 cells).
  const cells = useMemo(() => {
    const firstDow = new Date(cursor.y, cursor.m, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const prevDays = new Date(cursor.y, cursor.m, 0).getDate();
    const out = [];
    // Leading blanks from previous month
    for (let i = firstDow - 1; i >= 0; i--) {
      const d = prevDays - i;
      const y = cursor.m === 0 ? cursor.y - 1 : cursor.y;
      const m = cursor.m === 0 ? 11 : cursor.m - 1;
      out.push({ y, m, d, muted: true, key: keyOf(y, m, d) });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      out.push({ y: cursor.y, m: cursor.m, d, muted: false, key: keyOf(cursor.y, cursor.m, d) });
    }
    // Trailing blanks to fill 42
    while (out.length < 42) {
      const last = out[out.length - 1];
      const nx = new Date(last.y, last.m, last.d + 1);
      out.push({ y: nx.getFullYear(), m: nx.getMonth(), d: nx.getDate(), muted: true, key: keyOf(nx.getFullYear(), nx.getMonth(), nx.getDate()) });
    }
    return out;
  }, [cursor]);

  const clearSelection = () => onSelectDate && onSelectDate(null);

  return (
    <div className="glass-card rounded-2xl overflow-hidden" data-testid="media-calendar-widget">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between gap-2 border-b t-border">
        <div className="flex items-center gap-1.5">
          <CalendarIcon className="w-4 h-4 text-[#D98A94]" />
          <h3 className="text-sm font-bold t-text">{isID ? 'Kalender Acara' : 'Event Calendar'}</h3>
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#D98A94]/10 text-[#C0727C] text-[10px] font-bold">
            {eventCountThisMonth}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={goPrev}
            aria-label={isID ? 'Bulan sebelumnya' : 'Previous month'}
            data-testid="cal-prev"
            className="w-8 h-8 rounded-full hover:bg-white/60 t-text-secondary flex items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goToday}
            data-testid="cal-today"
            className="text-[11px] font-bold t-text px-2.5 py-1 rounded-full hover:bg-white/60 min-w-[132px]"
          >
            {MONTH_LABELS[cursor.m]} {cursor.y}
          </button>
          <button
            onClick={goNext}
            aria-label={isID ? 'Bulan berikutnya' : 'Next month'}
            data-testid="cal-next"
            className="w-8 h-8 rounded-full hover:bg-white/60 t-text-secondary flex items-center justify-center"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 border-b t-border bg-white/40">
        {DAY_LABELS.map((lbl, i) => (
          <div
            key={lbl}
            className={`py-2 text-[10px] font-bold text-center uppercase tracking-wider ${i === 0 ? 'text-[#D98A94]' : 't-text-muted'}`}
          >
            {lbl}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-px bg-[#F5E7EA]/40" data-testid="cal-days-grid">
        {cells.map((c) => {
          const isToday = !c.muted && c.y === today.y && c.m === today.m && c.d === today.d;
          const isSelected = selectedDate === c.key;
          const dayEvents = eventsByDate.get(c.key) || [];
          const hasEvents = dayEvents.length > 0 && !c.muted;
          const cellBg = isSelected
            ? 'bg-[#D98A94] text-white'
            : c.muted
              ? 'bg-white/30 t-text-muted'
              : hasEvents
                ? 'bg-white hover:bg-[#D98A94]/8 cursor-pointer'
                : 'bg-white/70 hover:bg-white cursor-pointer';
          const dotSet = new Set();
          dayEvents.slice(0, 3).forEach(e => dotSet.add(TYPE_COLOR[e.event_type] || '#94A3B8'));
          return (
            <button
              key={`${c.key}-${c.muted ? 'm' : 'a'}`}
              type="button"
              disabled={c.muted}
              onClick={() => hasEvents && onSelectDate && onSelectDate(isSelected ? null : c.key)}
              className={`relative min-h-[52px] flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${cellBg} ${isToday && !isSelected ? 'ring-2 ring-[#D98A94]/60 ring-inset' : ''}`}
              data-testid={`cal-cell-${c.key}`}
              aria-label={`${c.d} ${MONTH_LABELS[c.m]} ${c.y}${hasEvents ? ` · ${dayEvents.length} ${isID ? 'acara' : 'events'}` : ''}`}
              aria-pressed={isSelected}
            >
              <span className={`text-xs font-semibold ${isSelected ? 'text-white' : c.muted ? 'opacity-40' : hasEvents ? 't-text' : 't-text-secondary'}`}>
                {c.d}
              </span>
              {hasEvents && (
                <div className="flex gap-0.5 items-center">
                  {Array.from(dotSet).slice(0, 3).map(color => (
                    <span
                      key={color}
                      className="w-1 h-1 rounded-full"
                      style={{ background: isSelected ? '#fff' : color }}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <span className={`text-[8px] font-bold ${isSelected ? 'text-white/90' : 'text-[#C0727C]'}`}>
                      +{dayEvents.length - 3}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend + selection indicator */}
      <div className="px-4 py-2.5 flex items-center flex-wrap gap-2 justify-between border-t t-border bg-white/40 text-[10px]">
        <div className="flex items-center gap-1.5 flex-wrap t-text-muted">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#D98A94]" />
            Kebaktian / Puja
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
            Meditasi
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#8B5CF6]" />
            Dharma
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
            Perayaan
          </span>
        </div>
        {selectedDate && (
          <button
            onClick={clearSelection}
            data-testid="cal-clear-filter"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D98A94]/10 text-[#C0727C] font-bold hover:bg-[#D98A94]/20"
          >
            <X className="w-3 h-3" />
            {isID ? 'Hapus filter tanggal' : 'Clear date filter'}
          </button>
        )}
      </div>
    </div>
  );
}
